import { MeasuredToken } from "./LayoutMeasurer";
import { Token } from "./BuildTokens";

export type ProcessedChar = {
  char: string;
  rect: DOMRect;
  token: Token;
  charIndexGlobal: number;
  charIndexInLine: number;
  charIndexInWord: number;
  isBeforeElement?: boolean;
  isAfterElement?: boolean;
};

export type ProcessedWord = {
  chars: ProcessedChar[];
  rect: DOMRect;
  wordIndexGlobal: number;
  wordIndexInLine: number;
  isBeforeElement?: boolean;
  isAfterElement?: boolean;
};

export type LayoutLine = {
  words: ProcessedWord[];
  rect: DOMRect;
  lineIndex: number;
  isBeforeElement?: boolean;
  isAfterElement?: boolean;
};

const LINE_TOLERANCE = 1;

export function SplitMeasuredTokens(
  measured: MeasuredToken[]
): LayoutLine[] {
  const lines: LayoutLine[] = [];
  let currentLine: LayoutLine | null = null;
  let y = 0;

  let charIndexGlobal = 0;
  let wordIndexGlobal = 0;

  measured.forEach((mt) => {
    const token = mt.token;
    const isBefore =
      token.meta?.isBeforeElement ?? false;
    const isAfter =
      token.meta?.isAfterElement ?? false;

    if (token.type === "br") {
      currentLine = null;
      return;
    }

    if (token.type === "text") {
      const segments =
        token.content.match(/(\s+|\S+)/g);
      if (!segments) return;

      let offsetX = mt.rect.left;
      const contentLength = token.content.length;
      const avgCharWidth =
        contentLength > 0
          ? mt.rect.width / contentLength
          : 0;

      segments.forEach((segment) => {
        if (/^\s+$/.test(segment)) {
          offsetX +=
            avgCharWidth * segment.length;
          return;
        }

        const chars: ProcessedChar[] = [];
        segment.split("").forEach((char, i) => {
          const rect = new DOMRect(
            offsetX,
            mt.rect.top,
            avgCharWidth,
            mt.rect.height
          );
          chars.push({
            char,
            rect,
            token,
            charIndexInWord: i,
            charIndexInLine: 0,
            charIndexGlobal: charIndexGlobal++,
          });
          offsetX += avgCharWidth;
        });

        if (chars.length > 0) {
          const lastChar =
            chars[chars.length - 1];
          if (isBefore)
            lastChar.isBeforeElement = true;
          if (isAfter)
            lastChar.isAfterElement = true;
        }

        const isNewLine =
          !currentLine ||
          Math.abs(mt.rect.top - y) >
            LINE_TOLERANCE;
        if (isNewLine) {
          y = mt.rect.top;
          currentLine = {
            words: [],
            rect: mt.rect,
            lineIndex: lines.length,
          };
          lines.push(currentLine);
        }

        if (!currentLine) return;

        const wordIndexInLine =
          currentLine.words.length;
        const charsBefore =
          currentLine.words.reduce(
            (acc, w) => acc + w.chars.length,
            0
          );
        chars.forEach(
          (c, i) =>
            (c.charIndexInLine = charsBefore + i)
        );

        const processedWord: ProcessedWord = {
          chars,
          rect: mt.rect,
          wordIndexGlobal: wordIndexGlobal++,
          wordIndexInLine,
          isBeforeElement: isBefore,
          isAfterElement: isAfter,
        };

        currentLine.words.push(processedWord);
        currentLine.rect = mergeRects(
          currentLine.words.map((w) => w.rect)
        );
        if (isBefore)
          currentLine.isBeforeElement = true;
        if (isAfter)
          currentLine.isAfterElement = true;
      });
    } else if (token.type === "element") {
      const rect = mt.rect;
      const isNewLine =
        !currentLine ||
        Math.abs(rect.top - y) > LINE_TOLERANCE;

      if (isNewLine) {
        y = rect.top;
        currentLine = {
          words: [],
          rect,
          lineIndex: lines.length,
        };
        lines.push(currentLine);
      }

      if (!currentLine) return;

      const wordIndexInLine =
        currentLine.words.length;
      const charsBefore =
        currentLine.words.reduce(
          (acc, w) => acc + w.chars.length,
          0
        );

      const chars: ProcessedChar[] = [
        {
          char: "[E]",
          rect,
          token,
          charIndexInWord: 0,
          charIndexInLine: charsBefore,
          charIndexGlobal: charIndexGlobal++,
        },
      ];

      const processedWord: ProcessedWord = {
        chars,
        rect,
        wordIndexGlobal: wordIndexGlobal++,
        wordIndexInLine,
        isBeforeElement: false,
        isAfterElement: false,
      };

      currentLine.words.push(processedWord);
      currentLine.rect = mergeRects(
        currentLine.words.map((w) => w.rect)
      );
    }
  });

  return lines;
}

function mergeRects(rects: DOMRect[]): DOMRect {
  const left = Math.min(
    ...rects.map((r) => r.left)
  );
  const top = Math.min(
    ...rects.map((r) => r.top)
  );
  const right = Math.max(
    ...rects.map((r) => r.right)
  );
  const bottom = Math.max(
    ...rects.map((r) => r.bottom)
  );
  return new DOMRect(
    left,
    top,
    right - left,
    bottom - top
  );
}
